import React, { useEffect, useState } from 'react';
import { DefaultToolCallRenderer } from "../ToolRenderers";
import { ToolRendererProps } from "../ToolRenderers";
import { FlameChartNode, FlameChartNodes, FlameChartPlugin, TimeframeSelectorPlugin } from "../../flame-chart-js";
import { FlameChartContainerComponent } from "../../flame-chart-js/wrappers/react/flame-chart-container-component";
import { Input } from "../../ui/input";
import { useDebouncedCallback } from "use-debounce";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";

// The response is the flame chart node directly
type FlameChartNodeResponse = FlameChartNode;

// Helper function to set highlighted search in flame chart nodes
function setHiglightedSearch(flameChartNode: FlameChartNode, searchString: string) {
  flameChartNode.color = searchString === "" ? "#435b94" : flameChartNode.name.toLowerCase().includes(searchString.toLowerCase()) ? "#3793FF" : "#435b94";
  flameChartNode.children?.forEach((child) => {
      setHiglightedSearch(child, searchString);
  });
}

// Helper function to set highlighted nodes based on search
function setHighlightedSearch(searchString: string, flameChartNodes: FlameChartNodes, setFlameChartNodes: React.Dispatch<React.SetStateAction<FlameChartNodes>>) {
  if (flameChartNodes.length === 0) {
      setFlameChartNodes([]);
      return;
  }
  setFlameChartNodes(() => {
      flameChartNodes.forEach((node) => {
          setHiglightedSearch(node, searchString);
      })
      return [...flameChartNodes];
  })
}

export function ProfilesToolRenderer({ message, isToolCall }: ToolRendererProps) {
  // State for search and flame chart
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [flameChartNodes, setFlameChartNodes] = useState<FlameChartNodes>([]);
  const [highlightedChartNodes, setHighlightedChartNodes] = useState<FlameChartNodes>([]);
  const debouncedSearchHighlight = useDebouncedCallback(setHighlightedSearch, 100);
  const [queryCompleted, setQueryCompleted] = useState<boolean>(false);

  // Initialize flame chart plugins
  const [flameChartPlugins] = useState<any[]>(
    () => {
      let flameChartPlugin = new FlameChartPlugin({
        name: "flameChart",
        data: [],
      });

      let timeframeSelectorPlugin = new TimeframeSelectorPlugin({
        name: "timeframeSelector",
        flameChartNodes: [],
        settings: {
          styles: {
            graphStrokeColor: "#3793FF",
          },
        },
      });
      
      return [timeframeSelectorPlugin, flameChartPlugin];
    }
  );

  // We only want to render the response, not the call
  if (!message.tool_call_response_content) {
    // This is a tool call, do the default rendering
    return <DefaultToolCallRenderer message={message} />;
  }

  let profileData: FlameChartNodeResponse | null = null;
  try {
    profileData = JSON.parse(message.tool_call_response_content);
  } catch (error) {
    console.error('Error parsing profiles response data:', error);
    return null;
  }

  // Validate response
  if (!profileData || !profileData.name) {
    return null;
  }

  // Set flame chart nodes if not already set
  useEffect(() => {
    if (flameChartNodes.length === 0 && profileData) {
      setFlameChartNodes([profileData]);
      setQueryCompleted(true);
    }
  }, [profileData]);

  // Update highlighting when search term changes
  useEffect(() => {
    debouncedSearchHighlight(searchTerm, flameChartNodes, setHighlightedChartNodes);
  }, [searchTerm, flameChartNodes]);

  // Update plugins when highlighted nodes change
  useEffect(() => {
    if (highlightedChartNodes.length === 0) {
      return;
    }
    flameChartPlugins[0].setFlameChartNodes(highlightedChartNodes);
    flameChartPlugins[1].setData(highlightedChartNodes);
  }, [highlightedChartNodes]);

  return (
    <div className="w-full border rounded bg-backgrounddark">
      <div className="p-4 flex flex-col grow shrink min-w-0 min-h-0 overflow-y-auto h-[600px]">
        <div className="w-full h-[48px] mb-4 flex justify-between">
          <div className="flex gap-2">
            <Input 
              className="w-[256px]" 
              placeholder="Search function..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        {highlightedChartNodes.length === 0 && queryCompleted && (
          <div className="flex justify-center items-center h-full w-full">
            <div className="text-2xl font-semibold text-textmedium">
              No profiling data available
            </div>
          </div>
        )}
        
        {highlightedChartNodes.length > 0 && (
          <FlameChartContainerComponent
            settings={{
              styles: {
                main: {
                  backgroundColor: "#151f32",
                  fontColor: "#EBF1F7",
                }
              },
            }}
            plugins={flameChartPlugins}
            className="w-full flex grow shrink min-w-0 min-h-0"
          />
        )}
      </div>
    </div>
  );
} 